import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  clips,
  scoreAccionesClips,
  reglasClips,
  globalStatsClips,
  fuenteLabels,
  duracionLabels,
  getMasVistos,
  getMasCompartidos,
  getClipsEnTendencia,
  getMasRecientes,
  getClipsPorFuente,
  getClipsPorDuracion,
  formatearNumero,
  coverImage,
  type Clip,
  type FuenteClip,
  type DuracionClip,
} from '@/mocks/clips';

type Ordenamiento = 'tendencia' | 'recientes' | 'mas-vistos' | 'mas-compartidos' | 'score';
type Vista = 'clips' | 'reglas';

export default function ClipsPage() {
  const [vista, setVista] = useState<Vista>('clips');
  const [ordenamiento, setOrdenamiento] = useState<Ordenamiento>('tendencia');
  const [fuenteFiltro, setFuenteFiltro] = useState<FuenteClip | 'todas'>('todas');
  const [duracionFiltro, setDuracionFiltro] = useState<DuracionClip | 'todas'>('todas');
  const [clipSeleccionado, setClipSeleccionado] = useState<Clip | null>(null);

  const tendencia = getClipsEnTendencia();
  const masVistos = getMasVistos();
  const masCompartidos = getMasCompartidos();
  const recientes = getMasRecientes();

  const clipsFiltrados = (() => {
    let resultado: Clip[] = [];
    switch (ordenamiento) {
      case 'tendencia': resultado = [...clips].sort((a, b) => (b.esTendencia ? 1 : 0) - (a.esTendencia ? 1 : 0) || b.vistas - a.vistas); break;
      case 'recientes': resultado = recientes; break;
      case 'mas-vistos': resultado = masVistos; break;
      case 'mas-compartidos': resultado = masCompartidos; break;
      case 'score': resultado = [...clips].sort((a, b) => b.scoreTotal - a.scoreTotal); break;
      default: resultado = [...clips];
    }
    if (fuenteFiltro !== 'todas') resultado = resultado.filter(c => c.fuente === fuenteFiltro);
    if (duracionFiltro !== 'todas') resultado = resultado.filter(c => c.duracion === duracionFiltro);
    return resultado;
  })();

  const ordenamientos: { id: Ordenamiento; label: string; icon: string }[] = [
    { id: 'tendencia', label: 'Tendencia', icon: 'ri-fire-line' },
    { id: 'recientes', label: 'Más Recientes', icon: 'ri-time-line' },
    { id: 'mas-vistos', label: 'Más Vistos', icon: 'ri-eye-line' },
    { id: 'mas-compartidos', label: 'Más Compartidos', icon: 'ri-share-forward-line' },
    { id: 'score', label: 'Mayor Score', icon: 'ri-flashlight-line' },
  ];

  const fuentePills: { fuente: FuenteClip | 'todas'; label: string; icono: string }[] = [
    { fuente: 'todas', label: 'Todas las Fuentes', icono: 'ri-filter-line' },
    { fuente: 'programa', label: 'Programas', icono: 'ri-mic-line' },
    { fuente: 'locutor', label: 'Locutores', icono: 'ri-user-voice-line' },
    { fuente: 'entrevista', label: 'Entrevistas', icono: 'ri-chat-1-line' },
    { fuente: 'evento', label: 'Eventos', icono: 'ri-calendar-event-line' },
    { fuente: 'concurso', label: 'Concursos', icono: 'ri-trophy-line' },
  ];

  const duracionPills: { duracion: DuracionClip | 'todas'; label: string }[] = [
    { duracion: 'todas', label: 'Todas las Duraciones' },
    { duracion: '15', label: '15s' },
    { duracion: '30', label: '30s' },
    { duracion: '60', label: '60s' },
    { duracion: '180', label: '3 min' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
        <img
          src={coverImage}
          alt="Sistema de Clips RFW"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-500/80 text-white text-xs font-bold mb-4 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
            CLIPS
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white text-center mb-3 max-w-2xl drop-shadow-lg">
            Contenido Viral que Impulsa Descubrimiento
          </h1>
          <p className="text-white/80 text-sm md:text-base max-w-xl text-center leading-relaxed">
            Clips de 15 segundos a 3 minutos de programas, entrevistas, eventos, concursos y locutores. Cada clip es una puerta a nuevas radios.
          </p>
        </div>

        {/* Stats overlay */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-6 md:gap-10 pb-6">
          {[
            { label: 'Clips', valor: globalStatsClips.totalClips.toString() },
            { label: 'En Tendencia', valor: globalStatsClips.clipsEnTendencia.toString() },
            { label: 'Vistas Totales', valor: formatearNumero(globalStatsClips.totalVistas) },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-white font-bold text-lg md:text-xl">{s.valor}</p>
              <p className="text-white/60 text-[10px] md:text-xs">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Bar */}
      <div className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-xl border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-foreground-500 hover:text-foreground-950 text-xs font-medium whitespace-nowrap transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            RFW
          </Link>

          <div className="flex items-center gap-4 md:gap-6 text-[10px] md:text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
              {globalStatsClips.clipsEnTendencia} en tendencia
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-film-line text-[10px]"></i>
              {globalStatsClips.totalClips} clips
            </span>
            <span>Score: {formatearNumero(globalStatsClips.scoreTotalGenerado)}</span>
          </div>

          {/* Toggle Clips / Reglas */}
          <div className="flex items-center bg-background-100 rounded-full p-0.5">
            {([
              { id: 'clips' as Vista, label: 'Clips', icon: 'ri-film-line' },
              { id: 'reglas' as Vista, label: 'Reglas', icon: 'ri-scales-line' },
            ]).map(v => (
              <button
                key={v.id}
                onClick={() => setVista(v.id)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  vista === v.id ? 'bg-foreground-950 text-background-50' : 'text-foreground-500 hover:text-foreground-950'
                }`}
              >
                <i className={v.icon}></i>
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {vista === 'clips' ? (
          <>
            {/* Ordenamientos */}
            <div className="flex items-center gap-1.5 mb-5 overflow-x-auto pb-2 scrollbar-thin">
              {ordenamientos.map(o => (
                <button
                  key={o.id}
                  onClick={() => setOrdenamiento(o.id)}
                  className={`flex items-center gap-1 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    ordenamiento === o.id
                      ? 'bg-rose-500 text-white'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <i className={`${o.icon} text-[10px]`}></i>
                  {o.label}
                </button>
              ))}
            </div>

            {/* Filtros */}
            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {/* Fuente */}
              <div className="flex items-center gap-1 overflow-x-auto pb-1">
                {fuentePills.map(pill => (
                  <button
                    key={pill.fuente}
                    onClick={() => setFuenteFiltro(pill.fuente)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      fuenteFiltro === pill.fuente
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200/70'
                    }`}
                  >
                    <i className={`${pill.icono} text-[9px]`}></i>
                    {pill.label}
                  </button>
                ))}
              </div>
              <span className="text-foreground-300 text-[10px] hidden sm:inline">|</span>
              {/* Duración */}
              <div className="flex items-center gap-1 overflow-x-auto pb-1">
                {duracionPills.map(pill => (
                  <button
                    key={pill.duracion}
                    onClick={() => setDuracionFiltro(pill.duracion)}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      duracionFiltro === pill.duracion
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200/70'
                    }`}
                  >
                    {pill.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Grid */}
            {clipsFiltrados.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {clipsFiltrados.map(clip => {
                  const fuenteInfo = fuenteLabels[clip.fuente];
                  const durInfo = duracionLabels[clip.duracion];
                  return (
                    <div
                      key={clip.id}
                      onClick={() => setClipSeleccionado(clip)}
                      className="rounded-2xl bg-background-50 border border-background-200/70 hover:border-rose-200/60 transition-all duration-300 overflow-hidden group cursor-pointer"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={clip.imagenUrl}
                          alt={clip.titulo}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                            <i className="ri-play-fill text-foreground-950 text-xl ml-0.5"></i>
                          </div>
                        </div>
                        <div className="absolute top-2.5 right-2.5">
                          <span className={`px-2 py-0.5 rounded-md ${durInfo.bg} ${durInfo.color} text-[9px] font-bold`}>
                            {durInfo.label}
                          </span>
                        </div>
                        {clip.esTendencia && (
                          <div className="absolute top-2.5 left-2.5">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500 text-white text-[9px] font-bold">
                              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                              TENDENCIA
                            </span>
                          </div>
                        )}
                        <div className="absolute bottom-2.5 left-2.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${fuenteInfo.color} text-[9px] font-semibold`}>
                            <i className={`${fuenteInfo.icono} text-[9px]`}></i>
                            {fuenteInfo.label}
                          </span>
                        </div>
                      </div>
                      <div className="p-3 space-y-2">
                        <h3 className="text-foreground-950 font-bold text-xs leading-snug line-clamp-2">{clip.titulo}</h3>
                        <div className="flex items-center gap-2 text-[10px] text-foreground-500">
                          <span className="flex items-center gap-1">
                            <img src={clip.radioLogo} alt="" className="w-4 h-4 rounded-full object-cover" />
                            {clip.radioNombre}
                          </span>
                          {clip.locutorNombre && (
                            <>
                              <span className="text-foreground-300">·</span>
                              <span>{clip.locutorNombre}</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-3 pt-2 border-t border-background-100 text-[10px]">
                          <span className="text-foreground-400 flex items-center gap-1">
                            <i className="ri-eye-line text-[10px]"></i>
                            {formatearNumero(clip.vistas)}
                          </span>
                          <span className="text-foreground-400 flex items-center gap-1">
                            <i className="ri-share-forward-line text-[10px]"></i>
                            {formatearNumero(clip.compartidos)}
                          </span>
                          <span className="text-amber-600 font-semibold ml-auto flex items-center gap-0.5">
                            <i className="ri-flashlight-fill text-[10px]"></i>
                            {formatearNumero(clip.scoreTotal)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-film-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500 font-semibold mb-1">No se encontraron clips</p>
                <p className="text-foreground-400 text-xs mb-4">Probá cambiando los filtros</p>
                <button
                  onClick={() => { setFuenteFiltro('todas'); setDuracionFiltro('todas'); }}
                  className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Limpiar Filtros
                </button>
              </div>
            )}
          </>
        ) : (
          /* Vista Reglas */
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Reglas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {reglasClips.map(regla => (
                <div key={regla.id} className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${regla.gradiente} flex items-center justify-center mb-4`}>
                    <i className={`${regla.icono} text-white text-xl`}></i>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2">{regla.titulo}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{regla.descripcion}</p>
                </div>
              ))}
            </div>

            {/* Score section */}
            <div>
              <h3 className="text-lg font-bold text-foreground-950 mb-4 text-center">Acciones de Score</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                {scoreAccionesClips.map(item => (
                  <div key={item.id} className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mx-auto mb-3`}>
                      <i className={`${item.icono} text-white text-lg`}></i>
                    </div>
                    <p className="text-xl font-extrabold text-foreground-950">+{item.puntos}</p>
                    <p className="text-xs font-semibold text-foreground-700">{item.accion}</p>
                    <p className="text-[10px] text-foreground-400 mt-1.5 leading-relaxed">{item.descripcion}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { label: 'Clips', value: globalStatsClips.totalClips.toString(), icon: 'ri-film-line', color: 'text-rose-600 bg-rose-50' },
                { label: 'En Tendencia', value: globalStatsClips.clipsEnTendencia.toString(), icon: 'ri-fire-line', color: 'text-amber-600 bg-amber-50' },
                { label: 'Vistas', value: formatearNumero(globalStatsClips.totalVistas), icon: 'ri-eye-line', color: 'text-emerald-600 bg-emerald-50' },
                { label: 'Compartidos', value: formatearNumero(globalStatsClips.totalCompartidos), icon: 'ri-share-forward-line', color: 'text-violet-600 bg-violet-50' },
                { label: 'Score', value: formatearNumero(globalStatsClips.scoreTotalGenerado), icon: 'ri-flashlight-line', color: 'text-amber-600 bg-amber-50' },
              ].map((s, i) => (
                <div key={i} className="p-3 rounded-lg bg-background-50 border border-background-200/70 text-center">
                  <div className={`w-8 h-8 rounded-lg ${s.color} flex items-center justify-center mx-auto mb-1.5`}>
                    <i className={`${s.icon} text-sm`}></i>
                  </div>
                  <p className="text-base font-bold text-foreground-950">{s.value}</p>
                  <p className="text-[9px] text-foreground-500">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Top clips summary */}
            <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
              <h4 className="text-sm font-bold text-foreground-950 mb-3">Destacados</h4>
              <div className="space-y-2 text-xs text-foreground-600">
                <p><span className="font-semibold text-foreground-700">Clip más visto:</span> {globalStatsClips.clipMasVisto}</p>
                <p><span className="font-semibold text-foreground-700">Clip más compartido:</span> {globalStatsClips.clipMasCompartido}</p>
                <p><span className="font-semibold text-foreground-700">Radio con más clips:</span> {globalStatsClips.radioConMasClips}</p>
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 text-center">
          <Link
            to="/tv-app"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 text-background-50 font-bold text-sm cursor-pointer hover:from-rose-600 hover:to-amber-600 transition-all whitespace-nowrap"
          >
            <i className="ri-tv-line"></i>
            Ver en RFW TV
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla final */}
        <div className="mt-8 p-6 rounded-2xl bg-rose-50 border border-rose-200 text-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-compass-3-line text-xl text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">Los clips deben impulsar descubrimiento.</p>
          <p className="text-xs text-foreground-600 max-w-xl mx-auto">
            Cada clip es una puerta de entrada a nuevas radios, programas, locutores y eventos. El clip no es el destino — es el inicio del viaje. Un clic lleva al oyente a sintonizar, seguir o participar.
          </p>
        </div>
      </div>

      {/* Modal de detalle */}
      {clipSeleccionado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setClipSeleccionado(null)}>
          <div
            className="bg-background-50 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="relative h-56 overflow-hidden rounded-t-3xl">
              <img
                src={clipSeleccionado.imagenUrl}
                alt={clipSeleccionado.titulo}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <button
                onClick={() => setClipSeleccionado(null)}
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
              <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                {clipSeleccionado.esTendencia && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-rose-500 text-white text-[10px] font-bold">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                    TENDENCIA
                  </span>
                )}
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-[10px] font-semibold ${fuenteLabels[clipSeleccionado.fuente].color}`}>
                  <i className={`${fuenteLabels[clipSeleccionado.fuente].icono} text-[9px]`}></i>
                  {fuenteLabels[clipSeleccionado.fuente].label}
                </span>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <h2 className="text-lg font-bold text-foreground-950 leading-snug">{clipSeleccionado.titulo}</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">{clipSeleccionado.descripcion}</p>

              {/* Metadata grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-xl bg-background-100">
                  <p className="text-[9px] text-foreground-400 mb-0.5">Duración</p>
                  <p className="text-xs font-bold text-foreground-950">{clipSeleccionado.duracionSegundos}s <span className="text-foreground-400 font-normal">· {duracionLabels[clipSeleccionado.duracion].descripcion}</span></p>
                </div>
                <div className="p-3 rounded-xl bg-background-100">
                  <p className="text-[9px] text-foreground-400 mb-0.5">Radio</p>
                  <p className="text-xs font-bold text-foreground-950 flex items-center gap-1">
                    <img src={clipSeleccionado.radioLogo} alt="" className="w-4 h-4 rounded-full object-cover" />
                    {clipSeleccionado.radioNombre}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-background-100">
                  <p className="text-[9px] text-foreground-400 mb-0.5">Fecha</p>
                  <p className="text-xs font-bold text-foreground-950">{new Date(clipSeleccionado.fechaCreacion).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                </div>
                <div className="p-3 rounded-xl bg-background-100">
                  <p className="text-[9px] text-foreground-400 mb-0.5">Locutor</p>
                  <p className="text-xs font-bold text-foreground-950">{clipSeleccionado.locutorNombre || '—'}</p>
                </div>
              </div>

              {/* Stats counters */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-xl bg-rose-50 text-center">
                  <p className="text-lg font-extrabold text-rose-600">{formatearNumero(clipSeleccionado.vistas)}</p>
                  <p className="text-[9px] text-rose-500">Vistas</p>
                </div>
                <div className="p-3 rounded-xl bg-violet-50 text-center">
                  <p className="text-lg font-extrabold text-violet-600">{formatearNumero(clipSeleccionado.compartidos)}</p>
                  <p className="text-[9px] text-violet-500">Compartidos</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-50 text-center">
                  <p className="text-lg font-extrabold text-amber-600">{formatearNumero(clipSeleccionado.scoreTotal)}</p>
                  <p className="text-[9px] text-amber-500">Score</p>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-1.5">
                {clipSeleccionado.tags.map(tag => (
                  <span key={tag} className="px-2 py-1 rounded-md bg-background-100 text-[10px] text-foreground-500">#{tag}</span>
                ))}
              </div>

              {/* Action: Ver Radio */}
              {clipSeleccionado.fuenteUrl && (
                <Link
                  to={clipSeleccionado.fuenteUrl}
                  className="block w-full text-center px-6 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 text-background-50 font-bold text-sm cursor-pointer hover:from-rose-600 hover:to-amber-600 transition-all whitespace-nowrap"
                >
                  <i className="ri-link mr-1.5"></i>
                  Descubrir esta Radio
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}